import { useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import { Hourglass, Lock, PhoneCall } from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { ToastAction } from '@/components/ui/toast';
import { useFeatureGate } from '@/hooks/useFeatureGate';
import { gateErrorPrompt } from '@/lib/gateErrors';

const NOTICE = "Someone's waiting to talk to you";

/** Two short rising tones; no audio file needed. */
function chime() {
  try {
    const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    [0, 0.25].forEach((at, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.frequency.value = i ? 880 : 660;
      g.gain.setValueAtTime(0.0001, ctx.currentTime + at);
      g.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + at + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + at + 0.22);
      o.connect(g).connect(ctx.destination);
      o.start(ctx.currentTime + at);
      o.stop(ctx.currentTime + at + 0.25);
    });
    setTimeout(() => ctx.close?.(), 1000);
  } catch { /* audio blocked */ }
}

/**
 * "Hold for me" on an outbound call: the server holds the user's leg with
 * silence, keeps the far end in the conference and listens for a person. When
 * one speaks: chime + toast (+ web push from the server) -> Return to call.
 * Starter and Growth; other plans see the upgrade CTA (the server gates too).
 */
export function HoldForMeButton({ callSid, className = '' }: { callSid: string | undefined; className?: string }) {
  const gate = useFeatureGate('holdForMe');
  const { toast } = useToast();
  const [state, setState] = useState<'idle' | 'starting' | 'holding' | 'detected' | 'returning'>('idle');
  const socketRef = useRef<Socket | null>(null);
  const notified = useRef(false);

  const returnToCall = async () => {
    if (!callSid) return;
    setState('returning');
    try {
      await apiRequest('POST', `/api/voice/incall/${encodeURIComponent(callSid)}/hold-for-me`, { action: 'stop' });
      setState('idle');
    } catch (e: any) {
      setState('holding');
      toast({ title: 'Could not bring you back', description: e?.message || 'Please try again.', variant: 'destructive' });
    }
  };

  const onDetected = (text?: string | null) => {
    if (notified.current) return;
    notified.current = true;
    setState('detected');
    chime();
    toast({
      title: NOTICE,
      description: text ? `“${text}”` : 'Return to call',
      duration: 60_000,
      action: <ToastAction altText="Return to call" onClick={() => { void returnToCall(); }}>Return to call</ToastAction>,
    });
    try {
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted' && document.hidden) {
        const n = new Notification(NOTICE, { body: 'Return to call', tag: `hold-for-me-${callSid}`, requireInteraction: true });
        n.onclick = () => { window.focus(); n.close(); };
      }
    } catch { /* notifications blocked */ }
  };

  // While holding: socket event, with polling as the fallback.
  useEffect(() => {
    if (!callSid || state !== 'holding') return;
    let token: string | null = null;
    try { token = localStorage.getItem('token'); } catch { /* storage blocked */ }
    if (token) {
      const socket = io({ auth: { token }, reconnectionAttempts: 5, timeout: 10000 });
      socketRef.current = socket;
      socket.on('connect', () => socket.emit('auth', { token }));
      socket.on('call:hold-for-me', (msg: any) => { if (msg?.callSid === callSid && msg.detected) onDetected(msg.text); });
    }
    const poll = setInterval(() => {
      apiRequest('GET', `/api/voice/incall/${encodeURIComponent(callSid)}/hold-for-me`)
        .then((r: any) => { if (r?.detected) onDetected(r.text); else if (r && !r.active) setState('idle'); })
        .catch(() => {});
    }, 4000);
    return () => { clearInterval(poll); socketRef.current?.disconnect(); socketRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callSid, state]);

  // Call ended while holding: the server watch dies with the conference.
  useEffect(() => { if (!callSid) { setState('idle'); notified.current = false; } }, [callSid]);

  if (!callSid) return null;

  const start = async () => {
    if (!gate.allowed) { gate.openUpgrade(); return; }
    setState('starting');
    notified.current = false;
    try {
      await apiRequest('POST', `/api/voice/incall/${encodeURIComponent(callSid)}/hold-for-me`, { action: 'start' });
      setState('holding');
      try { if (typeof Notification !== 'undefined' && Notification.permission === 'default') void Notification.requestPermission(); } catch { /* ignore */ }
    } catch (e: any) {
      setState('idle');
      const prompt = gateErrorPrompt(e);
      if (prompt?.upgrade) { gate.openUpgrade(); return; }
      toast({ title: 'Hold for me did not start', description: e?.message || 'Please try again.', variant: 'destructive' });
    }
  };

  if (state === 'holding' || state === 'detected' || state === 'returning') {
    const detected = state === 'detected';
    return (
      <div className={`rounded-xl px-3 py-2.5 flex items-center gap-3 ${detected ? 'bg-emerald-50 dark:bg-emerald-500/15' : 'bg-amber-50 dark:bg-amber-500/15'} ${className}`} role="status" data-testid="hold-for-me-active">
        {detected ? <PhoneCall className="h-5 w-5 text-emerald-600 shrink-0 animate-pulse" aria-hidden /> : <Hourglass className="h-5 w-5 text-amber-600 shrink-0" aria-hidden />}
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold ${detected ? 'text-emerald-800 dark:text-emerald-200' : 'text-amber-800 dark:text-amber-200'}`}>{detected ? NOTICE : 'Holding for you'}</p>
          {!detected && <p className="text-xs text-amber-700/80 dark:text-amber-200/80">You're muted and won't hear the hold music. We'll ping you when someone picks up.</p>}
        </div>
        <button type="button" onClick={returnToCall} disabled={state === 'returning'} className="shrink-0 h-9 px-3 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold disabled:opacity-60" data-testid="hold-for-me-return">
          Return to call
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={start}
      disabled={state === 'starting'}
      className={`w-full h-10 rounded-2xl bg-slate-100 hover:bg-slate-200 text-slate-700 dark:bg-muted dark:text-foreground dark:hover:bg-muted/80 text-sm font-medium flex items-center justify-center gap-2 transition disabled:opacity-60 ${className}`}
      data-testid="button-hold-for-me"
    >
      {gate.allowed ? <Hourglass className="h-4 w-4" aria-hidden /> : <Lock className="h-4 w-4" aria-hidden />}
      {state === 'starting' ? 'Starting…' : 'Hold for me'}
      {!gate.allowed && gate.requiredPlanLabel && <span className="rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300 px-1.5 py-0.5 text-[10px] font-semibold">{gate.requiredPlanLabel}</span>}
    </button>
  );
}
